// Administrador Master → Empresas (ADR-0047): list with filters and the company file — profile, review
// decision (status ≠ credit), SUPER_ADMIN commercial terms, users, campaigns, orders, holds and the
// administrative history. Every action is audited by the database.
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type SyntheticEvent } from "react";
import { toast } from "sonner";
import { ApiProblem } from "@/api/errors";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { Field } from "@/ui/field";
import { Input, Textarea } from "@/ui/input";
import { Callout, Details, EmptyState, PageHeader, Panel, Section, Table, Td, Th } from "@/ui/layout";
import { formatDateTime } from "@/lib/format";
import { PageError, PageLoading, useRouteParams, useTitle } from "../common/page";
import {
  AUTH_STATUS,
  COMPANY_STATUS,
  countryName,
  ORG_TYPE_LABELS,
  PAYMENT_MODE_LABELS,
  PO_STATUS,
  ruleMessage,
  usd,
  formField,
} from "../commercial/labels";
import { adminPost, useAdmin, useAdminMutation, useCompanyOverview } from "./admin-api";
import { useOperatorRoles } from "./admin-layout";

const select =
  "h-8 rounded-md border border-line-strong bg-surface px-2 text-sm text-fg focus-visible:outline-2 focus-visible:outline-brand";

interface CompanyRow {
  organizationId: string;
  name: string;
  orgType: string | null;
  legalName: string | null;
  country: string | null;
  status: string | null;
  duplicateTaxId: boolean | null;
  isTest: boolean | null;
  paymentMode: string;
  creditLimitMicros: number;
  users: number;
}

const problemText = (e: unknown) => (e instanceof ApiProblem ? ruleMessage(e) : "No se pudo completar la acción.");

export function AdminCompanies() {
  useTitle("Empresas");
  const search: { status?: string; orgType?: string } = useSearch({ strict: false });
  const navigate = useNavigate();
  const { data, isLoading, error } = useAdmin<{ items: CompanyRow[] }>("/admin/companies", {
    status: search.status,
    orgType: search.orgType,
  });
  const setFilter = (key: "status" | "orgType", value: string) =>
    void navigate({ to: "/admin/companies", search: { ...search, [key]: value || undefined } });
  return (
    <div className="grid gap-6">
      <PageHeader
        title="Empresas"
        description="Agencias, clientes directos y espacios existentes sin perfil empresarial."
      />
      <div className="flex flex-wrap gap-2">
        <select
          aria-label="Estado"
          className={select}
          value={search.status ?? ""}
          onChange={(e) => {
            setFilter("status", e.target.value);
          }}
        >
          <option value="">Todos los estados</option>
          {Object.entries(COMPANY_STATUS).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Tipo"
          className={select}
          value={search.orgType ?? ""}
          onChange={(e) => {
            setFilter("orgType", e.target.value);
          }}
        >
          <option value="">Agencias y clientes directos</option>
          <option value="AGENCY">Agencias</option>
          <option value="DIRECT_CLIENT">Clientes directos</option>
        </select>
      </div>
      {isLoading ? (
        <PageLoading />
      ) : error ? (
        <PageError error={error} />
      ) : !data?.items.length ? (
        <EmptyState title="Sin empresas con este filtro" />
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <thead>
              <tr>
                <Th>Empresa</Th>
                <Th>Tipo</Th>
                <Th>País</Th>
                <Th>Estado</Th>
                <Th>Modalidad</Th>
                <Th className="text-right">Crédito</Th>
                <Th className="text-right">Usuarios</Th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((c) => {
                const st = COMPANY_STATUS[c.status ?? "NO_PROFILE"];
                return (
                  <tr key={c.organizationId}>
                    <Td>
                      <Link
                        className="font-medium text-brand hover:underline"
                        to={`/admin/companies/${c.organizationId}`}
                      >
                        {c.legalName ?? c.name}
                      </Link>
                      {c.isTest ? <Badge className="ml-2">Prueba</Badge> : null}
                      {c.duplicateTaxId ? (
                        <Badge tone="warning" className="ml-2">
                          RTN duplicado
                        </Badge>
                      ) : null}
                    </Td>
                    <Td>{c.orgType ? ORG_TYPE_LABELS[c.orgType] : "—"}</Td>
                    <Td>{c.country ?? "—"}</Td>
                    <Td>{st ? <Badge tone={st.tone}>{st.label}</Badge> : c.status}</Td>
                    <Td>{PAYMENT_MODE_LABELS[c.paymentMode] ?? c.paymentMode}</Td>
                    <Td className="text-right tabular-nums">
                      {c.creditLimitMicros > 0 ? usd(c.creditLimitMicros) : "—"}
                    </Td>
                    <Td className="text-right tabular-nums">{c.users}</Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}

interface Overview {
  organization: { id: string; name: string; createdAt: string };
  profile: null | {
    org_type: string;
    legal_name: string;
    country: string;
    tax_id: string;
    tax_id_format_verified: boolean;
    duplicate_tax_id: boolean;
    website: string | null;
    contact_name: string;
    contact_email: string;
    contact_phone: string;
    address_line1: string | null;
    address_line2: string | null;
    city: string | null;
    region: string | null;
    postal_code: string | null;
    billing_contact_name: string | null;
    billing_contact_email: string | null;
    billing_contact_phone: string | null;
    billing_profile_complete: boolean;
    status: string;
    status_reason: string | null;
    reviewed_at: string | null;
    account_manager_user_id: string | null;
    internal_notes: string | null;
    is_test: boolean;
    submitted_at: string;
  };
  terms: null | {
    payment_mode: string;
    purchase_orders_enabled: boolean;
    credit_limit_micros: number;
    payment_terms_days: number;
    commercial_notes: string | null;
  };
  creditExposureMicros: number;
  users: Array<{ userId: string; email: string; name: string; role: string; status: string }>;
  campaigns: Array<{ id: string; name: string; archived: boolean }>;
  purchaseOrders: Array<{
    id: string;
    internal_ref: string;
    po_number: string;
    amount_micros: number;
    approved_amount_micros: number | null;
    status: string;
  }>;
  authorizations: Array<{
    id: string;
    campaign_id: string;
    requested_budget_micros: number;
    approved_budget_micros: number | null;
    status: string;
  }>;
  holds: Array<{
    id: string;
    scope: string;
    reason: string;
    source: string | null;
    createdAt: string;
    releasedAt: string | null;
  }>;
  history: Array<{ at: string; action: string; actorUserId: string | null; after: Record<string, unknown> | null }>;
}

const ACTION_LABELS: Record<string, string> = {
  "company.profile_submitted": "Registro empresarial enviado",
  "company.profile_updated": "Perfil actualizado por la empresa",
  "company.profile_edited_by_operator": "Perfil editado por Media Interactiva",
  "company.status_changed": "Cambio de estado",
  "commercial.terms_changed": "Condiciones comerciales",
  "commercial.cpm_rate_set": "Tarifa CPM",
  "commercial.purchase_order_submitted": "Orden de compra registrada",
  "commercial.purchase_order_approved": "Orden de compra aprobada",
  "commercial.purchase_order_rejected": "Orden de compra rechazada",
  "commercial.authorization_submitted": "Solicitud de campaña",
  "commercial.authorization_approved": "Campaña autorizada",
  "commercial.authorization_rejected": "Campaña rechazada",
  "commercial.budget_exhausted_hold": "Campaña detenida por presupuesto agotado",
};

export function AdminCompanyDetail() {
  const { orgId } = useRouteParams("orgId");
  const roles = useOperatorRoles();
  const { data, isLoading, error } = useCompanyOverview<Overview>(orgId);
  useTitle(data?.profile?.legal_name ?? "Empresa");
  if (isLoading) return <PageLoading />;
  if (error || !data) return <PageError error={error} />;
  const p = data.profile;
  const st = COMPANY_STATUS[p?.status ?? "NO_PROFILE"];
  const isSuper = roles.includes("SUPER_ADMIN");
  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow={<Link to="/admin/companies">Empresas</Link>}
        title={p?.legal_name ?? data.organization.name}
        description={`Espacio de trabajo creado el ${formatDateTime(data.organization.createdAt)}`}
        actions={st ? <Badge tone={st.tone}>{st.label}</Badge> : null}
      />
      {!p ? (
        <Callout title="Sin perfil empresarial">
          Este espacio de trabajo existía antes del registro empresarial. Su administrador puede completarlo desde
          Configuración; su acceso no se bloquea.
        </Callout>
      ) : (
        <>
          <Section title="Datos de la empresa">
            <Details
              items={[
                { label: "Tipo", value: ORG_TYPE_LABELS[p.org_type] ?? p.org_type },
                { label: "País", value: countryName(p.country) },
                {
                  label: "Identificación tributaria",
                  value: `${p.tax_id}${p.tax_id_format_verified ? "" : " · formato no verificado"}${p.duplicate_tax_id ? " · DUPLICADA" : ""}`,
                },
                { label: "Sitio web", value: p.website ?? "—" },
                { label: "Contacto", value: `${p.contact_name} · ${p.contact_email} · ${p.contact_phone}` },
                { label: "Registrada", value: formatDateTime(p.submitted_at) },
                { label: "Revisada", value: p.reviewed_at ? formatDateTime(p.reviewed_at) : "—" },
                { label: "Motivo", value: p.status_reason ?? "—" },
              ]}
            />
          </Section>
          <BillingProfile orgId={orgId} profile={p} />
          <StatusActions orgId={orgId} status={p.status} duplicate={p.duplicate_tax_id} />
          <InternalForm orgId={orgId} notes={p.internal_notes} isTest={p.is_test} />
        </>
      )}
      <Section
        title="Condiciones comerciales"
        description="El estado de la empresa no otorga crédito. Solo Super Admin configura estas condiciones."
      >
        <Details
          items={[
            { label: "Modalidad", value: PAYMENT_MODE_LABELS[data.terms?.payment_mode ?? "PREPAID"] },
            {
              label: "Órdenes de compra",
              value: data.terms?.purchase_orders_enabled ? "Habilitadas" : "No habilitadas",
            },
            { label: "Crédito aprobado", value: usd(data.terms?.credit_limit_micros ?? 0) },
            { label: "Comprometido", value: usd(data.creditExposureMicros) },
            { label: "Disponible", value: usd((data.terms?.credit_limit_micros ?? 0) - data.creditExposureMicros) },
            {
              label: "Condiciones de pago",
              value: data.terms
                ? data.terms.payment_terms_days
                  ? `${String(data.terms.payment_terms_days)} días`
                  : "Contado"
                : "—",
            },
          ]}
        />
        {isSuper && p ? <TermsForm orgId={orgId} terms={data.terms} /> : null}
      </Section>
      <Section title={`Usuarios (${String(data.users.length)})`}>
        <ul className="grid gap-1 text-sm">
          {data.users.map((u) => (
            <li key={u.userId}>
              {u.name} · <span className="text-muted">{u.email}</span> · {u.role}
              {u.status !== "ACTIVE" ? ` (${u.status})` : ""}
            </li>
          ))}
        </ul>
      </Section>
      <Section title={`Campañas (${String(data.campaigns.length)})`}>
        <ul className="grid gap-1 text-sm">
          {data.campaigns.slice(0, 30).map((c) => (
            <li key={c.id}>
              {c.name}
              {c.archived ? <span className="text-muted"> · archivada</span> : null}
            </li>
          ))}
        </ul>
      </Section>
      <Section title="Órdenes de compra y solicitudes">
        <div className="grid gap-2 text-sm">
          {data.purchaseOrders.map((po) => (
            <div key={po.id} className="flex flex-wrap gap-2">
              <span className="font-mono text-xs">{po.internal_ref}</span> · {po.po_number} · {usd(po.amount_micros)}
              <Badge tone={PO_STATUS[po.status]?.tone ?? "neutral"}>{PO_STATUS[po.status]?.label}</Badge>
            </div>
          ))}
          {data.authorizations.map((a) => (
            <div key={a.id} className="flex flex-wrap gap-2">
              Solicitud de campaña · {usd(a.requested_budget_micros)}
              <Badge tone={AUTH_STATUS[a.status]?.tone ?? "neutral"}>{AUTH_STATUS[a.status]?.label}</Badge>
            </div>
          ))}
          {data.purchaseOrders.length + data.authorizations.length === 0 ? (
            <span className="text-muted">Sin registros.</span>
          ) : null}
        </div>
      </Section>
      {data.holds.length > 0 ? (
        <Section title="Restricciones de entrega (facturación)">
          <ul className="grid gap-1 text-sm">
            {data.holds.map((h) => (
              <li key={h.id}>
                {h.reason} · {h.scope === "ORGANIZATION" ? "empresa" : "campaña"} · {formatDateTime(h.createdAt)}
                {h.releasedAt ? (
                  <span className="text-muted"> · liberada {formatDateTime(h.releasedAt)}</span>
                ) : (
                  <Badge tone="danger" className="ml-2">
                    Vigente
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      <Section title="Historial administrativo">
        <ul className="grid gap-1 text-sm">
          {data.history.map((h, i) => (
            <li key={i}>
              <span className="text-muted">{formatDateTime(h.at)}</span> · {ACTION_LABELS[h.action] ?? h.action}
              {h.after && typeof h.after.status === "string"
                ? ` → ${COMPANY_STATUS[h.after.status]?.label ?? h.after.status}`
                : ""}
              {h.after && typeof h.after.reason === "string" ? ` · ${h.after.reason}` : ""}
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
}

function StatusActions({ orgId, status, duplicate }: { orgId: string; status: string; duplicate: boolean }) {
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const m = useAdminMutation((s: "ACTIVE" | "SUSPENDED" | "REJECTED") =>
    adminPost.status(orgId, { status: s, reason: reason.trim() || null, acknowledgeDuplicate: ack })
  );
  const act = (s: "ACTIVE" | "SUSPENDED" | "REJECTED", ok: string) => {
    m.mutate(s, {
      onSuccess: () => {
        setReason("");
        toast.success(ok);
      },
      onError: (e) => toast.error(problemText(e)),
    });
  };
  return (
    <Section
      title="Validación"
      description="Rechazar o suspender exige un motivo; la empresa recibe la decisión por correo."
    >
      <Panel className="grid gap-3 p-4">
        <Field label="Motivo (obligatorio para rechazar o suspender)">
          <Input
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            maxLength={500}
          />
        </Field>
        {duplicate ? (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={ack}
              onChange={(e) => {
                setAck(e.target.checked);
              }}
            />{" "}
            Revisé la identificación tributaria duplicada
          </label>
        ) : null}
        <div className="flex flex-wrap gap-2">
          {status !== "ACTIVE" ? (
            <Button
              variant="primary"
              disabled={m.isPending}
              onClick={() => {
                act("ACTIVE", "Empresa activada.");
              }}
            >
              {status === "SUSPENDED" ? "Reactivar" : "Validar empresa"}
            </Button>
          ) : null}
          {status === "PENDING_REVIEW" ? (
            <Button
              variant="secondary"
              disabled={m.isPending}
              onClick={() => {
                act("REJECTED", "Se pidieron correcciones.");
              }}
            >
              Pedir correcciones
            </Button>
          ) : null}
          {status === "ACTIVE" ? (
            <Button
              variant="danger"
              disabled={m.isPending}
              onClick={() => {
                act("SUSPENDED", "Operación comercial suspendida.");
              }}
            >
              Suspender operación comercial
            </Button>
          ) : null}
        </div>
      </Panel>
    </Section>
  );
}

/** Fiscal address and billing contact (CA-O3). Activation requires them; every edit is audited with a diff. */
function BillingProfile({ orgId, profile: p }: { orgId: string; profile: NonNullable<Overview["profile"]> }) {
  const [editing, setEditing] = useState(false);
  const m = useAdminMutation((body: Record<string, unknown>) => adminPost.patch(orgId, body));
  const fields = [
    ["addressLine1", "Dirección", p.address_line1, true],
    ["addressLine2", "Complemento", p.address_line2, false],
    ["city", "Ciudad", p.city, true],
    ["region", "Departamento, estado o provincia", p.region, true],
    ["postalCode", "Código postal", p.postal_code, false],
    ["billingContactName", "Nombre de facturación", p.billing_contact_name, true],
    ["billingContactEmail", "Correo de facturación", p.billing_contact_email, true],
    ["billingContactPhone", "Teléfono de facturación", p.billing_contact_phone, true],
  ] as const;
  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    const body: Record<string, unknown> = {};
    for (const [name, , current, required] of fields) {
      const value = formField(f, name).trim();
      if (value === (current ?? "")) continue;
      body[name] = value === "" && !required ? null : value;
    }
    m.mutate(body, {
      onSuccess: () => {
        toast.success("Datos de facturación guardados.");
        setEditing(false);
      },
      onError: (e) => toast.error(problemText(e)),
    });
  };
  return (
    <Section
      title="Facturación"
      description="Media Interactiva factura desde Honduras a la dirección fiscal de la empresa. Requerido para activarla."
      actions={
        <Badge tone={p.billing_profile_complete ? "success" : "warning"}>
          {p.billing_profile_complete ? "Completo" : "Incompleto"}
        </Badge>
      }
    >
      {editing ? (
        <form className="grid max-w-xl gap-3 sm:grid-cols-2" onSubmit={submit}>
          {fields.map(([name, label, current, required]) => (
            <Field key={name} label={label} hint={required ? undefined : "Opcional."}>
              <Input name={name} defaultValue={current ?? ""} maxLength={name === "postalCode" ? 12 : 200} />
            </Field>
          ))}
          <div className="flex gap-2 sm:col-span-2">
            <Button type="submit" variant="primary" disabled={m.isPending}>
              Guardar
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setEditing(false);
              }}
            >
              Cancelar
            </Button>
          </div>
        </form>
      ) : (
        <div className="grid gap-3">
          <Details
            items={[
              {
                label: "Dirección fiscal",
                value: p.address_line1
                  ? [p.address_line1, p.address_line2, p.city, p.region, p.postal_code].filter(Boolean).join(", ")
                  : "Pendiente",
              },
              {
                label: "Contacto de facturación",
                value: p.billing_contact_name
                  ? [p.billing_contact_name, p.billing_contact_email, p.billing_contact_phone]
                      .filter(Boolean)
                      .join(" · ")
                  : "Pendiente",
              },
            ]}
          />
          <div>
            <Button
              variant="secondary"
              onClick={() => {
                setEditing(true);
              }}
            >
              Editar datos de facturación
            </Button>
          </div>
        </div>
      )}
    </Section>
  );
}

function InternalForm({ orgId, notes, isTest }: { orgId: string; notes: string | null; isTest: boolean }) {
  const m = useAdminMutation((body: Record<string, unknown>) => adminPost.patch(orgId, body));
  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    m.mutate(
      { internalNotes: formField(f, "internalNotes"), isTest: f.get("isTest") === "on" },
      { onSuccess: () => toast.success("Guardado."), onError: (e) => toast.error(problemText(e)) }
    );
  };
  return (
    <Section title="Uso interno" description="Solo visible para Media Interactiva.">
      <form className="grid max-w-xl gap-3" onSubmit={submit}>
        <Field label="Observaciones comerciales">
          <Textarea name="internalNotes" defaultValue={notes ?? ""} maxLength={2000} />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" name="isTest" defaultChecked={isTest} /> Datos de prueba
        </label>
        <div>
          <Button type="submit" variant="secondary" disabled={m.isPending}>
            Guardar
          </Button>
        </div>
      </form>
    </Section>
  );
}

function TermsForm({ orgId, terms }: { orgId: string; terms: Overview["terms"] }) {
  const m = useAdminMutation((body: Parameters<typeof adminPost.terms>[1]) => adminPost.terms(orgId, body));
  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    m.mutate(
      {
        paymentMode: formField(f, "paymentMode") as "PREPAID" | "PURCHASE_ORDER" | "CREDIT",
        purchaseOrdersEnabled: f.get("purchaseOrdersEnabled") === "on",
        creditLimitUsd: formField(f, "creditLimitUsd") || "0",
        paymentTermsDays: Number(formField(f, "paymentTermsDays")),
        notes: formField(f, "notes") || null,
      },
      { onSuccess: () => toast.success("Condiciones guardadas."), onError: (e) => toast.error(problemText(e)) }
    );
  };
  return (
    <form className="grid max-w-xl gap-3 rounded-md border border-line p-4" onSubmit={submit}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Modalidad de pago">
          <select name="paymentMode" className={select} defaultValue={terms?.payment_mode ?? "PREPAID"}>
            {Object.entries(PAYMENT_MODE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Condiciones de pago">
          <select name="paymentTermsDays" className={select} defaultValue={String(terms?.payment_terms_days ?? 0)}>
            {[0, 15, 30, 45, 60].map((d) => (
              <option key={d} value={d}>
                {d === 0 ? "Contado" : `${String(d)} días`}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Crédito aprobado (USD)" hint="Máximo US$10,000 (política vigente). Nunca automático.">
          <Input
            name="creditLimitUsd"
            inputMode="decimal"
            defaultValue={((terms?.credit_limit_micros ?? 0) / 1_000_000).toFixed(2)}
          />
        </Field>
        <label className="flex items-center gap-2 self-end pb-1 text-sm">
          <input
            type="checkbox"
            name="purchaseOrdersEnabled"
            defaultChecked={terms?.purchase_orders_enabled ?? false}
          />{" "}
          Habilitar órdenes de compra
        </label>
      </div>
      <Field label="Observaciones">
        <Textarea name="notes" defaultValue={terms?.commercial_notes ?? ""} maxLength={2000} />
      </Field>
      <div>
        <Button type="submit" variant="primary" disabled={m.isPending}>
          Guardar condiciones
        </Button>
      </div>
    </form>
  );
}
